import { useRef } from "react";

import classnames from "classnames";

import { useSunsetLoginWithSonder } from "components/hooks/feature-flags";
import { useAnalyticsTracker } from "components/instrumentation/source/analytics";
import { useAppContext } from "components/store/source";
import { Button, Image, Link, Section, Text } from "components/ui/source";

import { csrfParam, csrfToken } from "../../shared/api/base_api";

import styles from "./Accounts.module.scss";

interface LoginOptionsFormPops {
  source?: string;
  forceLogout?: boolean;
  redirectPath?: string;
  buttonSource?: string;
}

const mediums = {
  bonvoy: "bonvoy",
  sonder: "sonder",
};

const config = {
  bonvoy: {
    redirectUrl: "/accounts/auth/cognito_marriott",
    imageUrl:
      "https://images.sonder.com/image/upload/c_fill,f_auto,w_200/v1731525946/sondercom/partners/marriott_bonvoy_logo_white",
    alt: "Marriott logo",
  },
  sonder: {
    redirectUrl: "",
    imageUrl:
      "https://images.sonder.com/image/upload/c_fill,f_auto,w_200/v1616091409/sondercom/press/logo/SONDER_LOGO_CREAM",
    alt: "Sonder logo",
  },
};

function LoginOptionsForm({
  source,
  redirectPath,
  forceLogout,
  buttonSource,
}: LoginOptionsFormPops) {
  const formRef = useRef(null);

  const { openLoginPopup } = useAppContext();
  const sunsetLoginWithSonder = useSunsetLoginWithSonder();

  const { analytics } = useAnalyticsTracker();

  async function onSubmit(medium) {
    if (!formRef.current) return;

    await trackLogInOptionsSubmit(medium);

    if (medium == mediums.bonvoy) {
      const redirectUrl = redirectPath ?? "/profile";

      formRef.current.method = "post";
      formRef.current.action = `${
        config.bonvoy.redirectUrl
      }?identity_provider=bonvoy-login&redirect_path=${encodeURIComponent(
        redirectUrl,
      )}${forceLogout ? "&signout=true" : ""}`;
      formRef.current.submit();
    } else {
      openLoginPopup({
        source: "Login Options",
        redirectPath,
      });
    }
  }

  const trackLogInOptionsSubmit = async medium => {
    try {
      await analytics.track("Login Options Select", {
        method: medium,
        source: source,
      });
    } catch {
      // do nothing
    }
  };

  const translateKey =
    buttonSource === "Log in"
      ? "modals.accounts.loginOptions.title"
      : "modals.accounts.loginOptions.titleMyStays";

  return (
    <>
      <form method="post" ref={formRef} data-testid="login-options-form">
        <input name={csrfParam()} value={csrfToken()} type="hidden" />
      </form>
      <Text
        id="loginOptionsFormHeading"
        element="h4"
        className={classnames(styles.header, styles.centered_text)}
        types={["headline4"]}
        translateKey={translateKey}
      />
      <Section className={classnames(styles.loginOptionsButtonsContainer)}>
        <Button
          data-testid={`${mediums.bonvoy}-button`}
          upperCase={false}
          className={classnames(styles.loginOptionsButton, styles.bonvoyButton)}
          onClick={() => onSubmit(mediums.bonvoy)}
        >
          <Image
            src={config["bonvoy"].imageUrl}
            alt={config["bonvoy"].alt}
            className={classnames(styles.loginOptionsButtonImage)}
          />
          <Text
            element="span"
            types={["body1", "off-white"]}
            translateKey={"modals.accounts.loginOptions.buttons.marriott"}
          />
        </Button>
        {sunsetLoginWithSonder ? null : (
          <Button
            data-testid={`${mediums.sonder}-button`}
            type="dark"
            upperCase={false}
            className={classnames(styles.loginOptionsButton)}
            onClick={() => onSubmit(mediums.sonder)}
          >
            <Image
              src={config["sonder"].imageUrl}
              alt={config["sonder"].alt}
              className={classnames(styles.loginOptionsButtonImage)}
            />
            <Text
              element="span"
              types={["body1"]}
              translateKey={"modals.accounts.loginOptions.buttons.sonder"}
            />
          </Button>
        )}
      </Section>
    </>
  );
}

export function LoginOptionsHelpCenter() {
  return (
    <Section
      className={classnames(
        styles.centered_text,
        styles.loginOptionsFooterText,
      )}
    >
      <Text
        element="span"
        types={["caption1"]}
        translateKey={"modals.accounts.loginOptions.footer.text"}
      />
      <Link
        translateKey={"modals.accounts.loginOptions.footer.link"}
        underline
        textTypes={["caption1"]}
        href={"/help"}
      />
    </Section>
  );
}

export default LoginOptionsForm;
